import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Box,
  Button,
  Column,
  ConfirmPopup,
  CustomSpinner,
  Icon,
  Row,
  Text,
} from '~/commonComponents';
import AnimatedCheckCircle from '~/components/shared/AnimatedCheckCircle';
import { useGetPostingPlans } from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { useNavigationDetector, useToggle } from '~/hooks';
import { WarningSolidIcon } from '~/images/icons';
import WarningIcon from '~/images/warning-icon.svg';
import { routes } from '~/routes';
import { Colors } from '~/themes/colors';
import {
  CreateOnsiteInterviewSteps,
  TCreateOnsiteInterviewPageLocationState,
} from '../CreateOnsiteInterview';
import {
  BasicInfoPreview,
  EarningsBenefitsPreview,
  InfoSection,
  JobDescriptionPreview,
} from '../JobDetailsForm';
import { SchedulePreview } from '../JobDetailsForm/components/ScheduleInfo';
import { formatExpectedDays } from '../JobDetailsForm/utils/formatters';
import { TermsConditionAddendumPopup } from '../components';
import {
  CreatePermanentJobPostingFormSection,
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageLocationState,
  TCreatePermanentJobPostingPageRoutes,
} from './Types';
import { PaymentDetailsPreview, PermanentJobsTips } from './components';
import { useDraftJobPost, useRequireTnCAddendum } from './hooks';
import { usePublishJobPost } from './hooks/usePublishJobPost';
import { firePermPostJobSubmitted, firePermPostJobAddInterviewsClicked } from '~/posthogs/events';

/**
 * Helper function to check if a navigation path belongs to the allowed flow routes
 */
const isPathInAllowedFlow = (pathname: string, currentId: string | undefined) => {
  if (!pathname || !currentId) return false;

  // Allow navigation to any path that starts with /permanent-jobs/postings/:id/create
  const allowedPattern = new RegExp(`^/permanent-jobs/postings/${currentId}/create`);

  return allowedPattern.test(pathname);
};

export const ReviewAndPaymentPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();

  const [isPublishSucceed, controlsPublishSucceed] = useToggle();
  const [isShowExitModalConfirmation, controlsExitModalConfirmation] = useToggle();
  const [isShowPaymentError, controlsShowPaymentError] = useToggle();
  const [isShowTnCAddendumPopup, controlsShowTnCAddendumPopup] = useToggle();

  // Navigation blocking logic
  const { allowNavigation, clearPendingNavigation, navigateToPending } = useNavigationDetector({
    when: true, // Always block navigation from this page
    onNavigationAttempt: (targetLocation) => {
      const targetPath = targetLocation?.pathname;

      // Check if target is within the allowed flow
      if (isPathInAllowedFlow(targetPath, id)) {
        // Allow navigation within the flow without confirmation
        navigateToPending();
      } else {
        // Show confirmation modal for navigation outside the flow
        controlsExitModalConfirmation.setOn();
      }
    },
  });

  const { draft, isLoading } = useDraftJobPost(id);
  const {
    data: postingPlans,
    isLoading: isLoadingPlans,
    isError: isErrorPlans,
    error: errorPlans,
  } = useGetPostingPlans();

  // Select the first plan by default
  const selectedPlan = postingPlans?.[0];

  const {
    doPublish,
    isLoading: isPublishing,
    isError: isPublishError,
    error: publishError,
  } = usePublishJobPost({
    jobPostingId: id,
    planId: selectedPlan?.id,
    onSuccess: () => {
      // Track the job submission event
      if (id && draft?.professionType && selectedPlan?.finalPrice) {
        firePermPostJobSubmitted({
          postingId: id,
          professionType: draft.professionType,
          listingFee: selectedPlan.finalPrice.toFixed(2), // Format as "450.00"
        });
      }

      controlsShowTnCAddendumPopup.setOff();
      controlsPublishSucceed.setOn();
    },
  });

  const { isAcceptingConsent, isConsentAccepted, doAcceptOfficeTnCPerm } = useRequireTnCAddendum({
    controls: controlsShowTnCAddendumPopup,
    onSucceedAcceptConsent: doPublish,
    jobPostingId: id,
  });

  useEffect(() => {
    if (isErrorPlans) {
      showErrorMessage({ error: errorPlans });
    }
  }, [isErrorPlans, errorPlans]);

  useEffect(() => {
    if (isPublishError) {
      if ((publishError as AxiosError<any>)?.response?.data?.errorCode === 'PAYMENT_FAILED') {
        controlsShowPaymentError.setOn();

        return;
      }

      showErrorMessage({ error: publishError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishError, isPublishError]);

  const handleEditClicked =
    (targetStep: CreatePermanentJobPostingSteps, scrollTo: CreatePermanentJobPostingFormSection) =>
    () => {
      if (isPublishing) {
        return;
      }

      navigate(`/permanent-jobs/postings/${id}/create/${targetStep}`, {
        state: {
          editMode: true,
          scrollTo,
        } satisfies TCreatePermanentJobPostingPageLocationState,
      });
    };

  const handleCloseSuccessPopup = () => {
    // Track Add Interviews clicked event
    if (id) {
      firePermPostJobAddInterviewsClicked({ postingId: id });
    }

    controlsPublishSucceed.setOff();
    allowNavigation();
    navigate(
      // eslint-disable-next-line max-len
      `/permanent-jobs/postings/${id}/onsite-interviews/create/${CreateOnsiteInterviewSteps.WorkDetails}`,
      {
        state: {
          isAfterJobPosting: true,
        } satisfies TCreateOnsiteInterviewPageLocationState,
      },
    );
  };

  const handlePostJobClicked = () => {
    if (!isConsentAccepted) {
      controlsShowTnCAddendumPopup.setOn();
      return;
    }

    doPublish();
  };

  if (isLoading || !draft || isLoadingPlans) {
    return <CustomSpinner isLoading />;
  }

  return (
    <>
      <Box alignSelf="center" gap={8} width={892}>
        <BasicInfoPreview
          jobPosting={draft}
          onEditClick={handleEditClicked(
            CreatePermanentJobPostingSteps.BasicInformation,
            CreatePermanentJobPostingFormSection.GeneralJobInformation,
          )}
        />
        <InfoSection
          icon="calendar-solid"
          title="Schedule"
          rightActionIcon="edit-pencil"
          onRightActionPress={handleEditClicked(
            CreatePermanentJobPostingSteps.WorkDetails,
            CreatePermanentJobPostingFormSection.Schedule,
          )}
        >
          <SchedulePreview
            scheduleType={draft.scheduleType || ''}
            expectedDays={formatExpectedDays(draft.expectedDays || [])}
          />
        </InfoSection>
        <EarningsBenefitsPreview
          jobPosting={draft}
          onEditClick={handleEditClicked(
            CreatePermanentJobPostingSteps.WorkDetails,
            CreatePermanentJobPostingFormSection.EarningsAndBenefits,
          )}
        />
        <InfoSection
          icon="document-solid"
          title="Job Description"
          rightActionIcon="edit-pencil"
          onRightActionPress={handleEditClicked(
            CreatePermanentJobPostingSteps.JobDescription,
            CreatePermanentJobPostingFormSection.JobDescription,
          )}
        >
          <JobDescriptionPreview description={draft.description || ''} />
        </InfoSection>
        <Box height={6} />
        <PermanentJobsTips />
        <Box height={20} />
        <PaymentDetailsPreview plan={selectedPlan} />
        <Row justifyContent="center" marginTop={6}>
          <Button
            text="Post Job"
            onClick={handlePostJobClicked}
            isLoading={isAcceptingConsent || isPublishing}
          />
        </Row>
      </Box>

      {isPublishSucceed && (
        // @ts-expect-error - ConfirmPopup is not typed
        <ConfirmPopup
          Icon={() => <AnimatedCheckCircle />}
          rightButtonAction={handleCloseSuccessPopup}
          rightButtonText="Add Onsite Interviews"
          rightButtonStyle={{ width: 296 }}
          title="Job Posted Successfully!"
          titleStyle={{ marginBottom: 10, marginTop: 14 }}
        >
          <Column gap={22}>
            <Text color={Colors.neutral_500} textAlign="center">
              Your job is live! To start receiving candidate applications,
              <br />
              post an onsite interview. These let you see candidates in
              <br />
              action before making a hiring decision.
            </Text>
            <Row
              backgroundColor={Colors.warning_30}
              borderColor={Colors.warning_700}
              borderStyle="solid"
              borderWidth={1}
              paddingBlock={12}
              paddingInline={10}
              gap={4}
              marginInline={32}
              marginBottom={38}
            >
              <Box width={24} height={24}>
                <WarningSolidIcon color={Colors.warning_500} size={24} />
              </Box>
              <Text color={Colors.warning_700}>
                Without onsite interviews, candidates cannot apply to your position.
              </Text>
            </Row>
          </Column>
        </ConfirmPopup>
      )}

      {isShowExitModalConfirmation && (
        // @ts-expect-error - ConfirmPopup is not typed
        <ConfirmPopup
          Icon={() => <Icon name="warning-outline" size={80} />}
          leftButtonAction={() => {
            clearPendingNavigation();
            controlsExitModalConfirmation.setOff();
          }}
          leftButtonText="Go Back"
          rightButtonAction={navigateToPending}
          rightButtonText="Leave Anyway"
          title="Are You Sure You Want to Leave?"
          titleStyle={{ marginBottom: 20, marginTop: 20 }}
          descriptionStyle={{ marginBottom: 40 }}
          description="Your job isn’t posted yet, but we saved it as a draft under Manage Jobs. Do you still want to leave this page?"
        />
      )}

      {isShowPaymentError && (
        // @ts-expect-error - ConfirmPopup is not typed
        <ConfirmPopup
          Icon={() => <img src={WarningIcon} alt="Warning" />}
          leftButtonStyle={{ width: 172 }}
          leftButtonAction={() => controlsShowPaymentError.setOff()}
          leftButtonText="Go Back"
          rightButtonStyle={{ width: 172 }}
          rightButtonAction={() => {
            allowNavigation();
            navigate(routes.paymentInformation);
          }}
          rightButtonText="Update Payment"
          title="Payment Failed - Job Not Posted"
          titleStyle={{ marginBottom: 20, marginTop: 20 }}
          descriptionStyle={{ marginBottom: 40 }}
          description="We couldn’t process your payment. Your job has been saved in your drafts. Please update your payment method and try posting again."
        />
      )}

      <TermsConditionAddendumPopup
        isLoading={isAcceptingConsent || isPublishing}
        isOpen={isShowTnCAddendumPopup}
        onConfirm={doAcceptOfficeTnCPerm}
        jobPostingId={id}
      />
    </>
  );
};
